import { DataType, UserPermission } from '@globaleris/ui.library'
import { Grid } from '@material-ui/core'
import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EnumCommodity } from '../../api'
import { EnumClientTypes } from '../../api/Argentia/enums/enumClientTypes'
import Page from '../../components/page/page'
import PageHeader from '../../components/page/pageHeader'
import CommodityFilter from '../../components/shared/commodityFilter/commodityFilter'
import CustomFilterV2 from '../../components/shared/customFilter/customFilterV2'
import DateFilter from '../../components/shared/dateFilter/dateFilter'
import TabsWithPermissions, { Tab } from '../../components/tabsWithPermissions/tabsWithPermissions'
import { MapToArray } from '../../helpers/arrayHelper/arrayHelper'
import { enumToMapValues, enumToSelectOptionArray, enumValueToKey } from '../../helpers/enumHelper'
import { hasPermission } from '../../helpers/permissionsHelper'
import { usePageState } from '../../hooks'
import { DateFilters, DatePeriod, GlobalNotification } from '../../model'
import { Resources } from '../../model/RBAC/constants'
import {
  getActiveCommodities,
  getActiveCommodity,
  storeActiveCommodity,
  storeActiveTabForCurrentPage,
  storeCommodityFilterForCurrentPage
} from '../../redux'
import { InvoicesTable } from './components/invoicesTable/invoicesTable'
import { mapTableConfiguration } from './invoicePage.mapper'
import { FilterDateBy, TabsInvoicesType } from './invoicesPage.Model'
import { fetchInvoices } from './invoicesPage.service'

export interface InvoicesPageProps {}

export const InvoicesPage: React.FC<InvoicesPageProps> = props => {
  const [t] = useTranslation(['globalerisLibrary', 'invoicesPage', 'enums'])

  const [isFetching, setIsFetching] = useState<boolean>(false)

  const { dataTableFilter, dataTableSearch, activeTab } = usePageState()

  const commodities = getActiveCommodities()
  const initialCommodityFilter: EnumCommodity =
    getActiveCommodity() ??
    (commodities !== undefined && commodities?.length > 0
      ? enumValueToKey(EnumCommodity, commodities[0])
      : EnumCommodity.GasNatural)

  const [currentTab, setCurrentTab] = useState<number>(activeTab ?? 0)

  const [globalNotification, setGlobalNotification] = useState<GlobalNotification | undefined>(
    undefined
  )

  const [dateRange, setDateRange] = React.useState<DatePeriod>({
    start: moment().toDate(),
    end: moment().toDate(),
    customFilter: {
      dateFilterType: FilterDateBy.EMISSION
    }
  })

  const [activeDateFilter, setActiveDateFilter] = useState<DateFilters | undefined>(
    DateFilters.TODAY
  )

  const [commodity, setCommodity] = useState<EnumCommodity>(initialCommodityFilter)
  const [filteredData, setFilteredData] = useState<any[] | undefined>(undefined)
  const [appliedCustomFilter, setAppliedCustomFilter] = useState<any>(undefined)
  const pendingFilterRef = React.useRef<any>(null)

  // We set an Array from the ClientTypes in order to have arranged both index (table tabs index, starting in 0) and values corresponding to each each clientType.
  const InvoiceTabsArray = MapToArray(enumToMapValues(TabsInvoicesType))

  const handleTabPermissions = (tab: TabsInvoicesType) => {
    let resources: Resources[]

    switch (tab) {
      case TabsInvoicesType.aggregatedInvoice_finalClient:
        resources = [Resources.GroupedBilling, Resources.Client]
        break
      case TabsInvoicesType.aggregatedInvoice_extendedClient:
        resources = [Resources.GroupedBilling, Resources.ExtendedClients]
        break
      case TabsInvoicesType.extendedClient:
        resources = [Resources.ExtendedClients]
        break
      case TabsInvoicesType.finalClient:
        resources = [Resources.Client]
        break
      default:
        resources = [Resources.InvoicesPage]
        break
    }
    return {
      hasPermission: resources.every(resource =>
        hasPermission(resource, [UserPermission.EDIT, UserPermission.READ_ONLY])
      )
    }
  }

  const tabsDefinition: Map<any, Tab> = new Map(
    InvoiceTabsArray.map((tab, tabIndex) => [
      tabIndex,
      {
        label: t(`invoicesPage:tab.${tab.name}`),
        ...handleTabPermissions(tab.value),
        id: tab.value
      }
    ])
  )
  // filter tabs with permissions to avoid getting hidden tabs values onTabChange (US #60896 - task #61085)
  const tabsArray = Array.from(tabsDefinition.values()).filter(tab => tab.hasPermission)

  // Hooks Region Starts

  useEffect(() => {
    if (activeTab !== undefined) {
      setCurrentTab(activeTab)
    } else {
      storeActiveTabForCurrentPage(0)
    }
  }, [activeTab])

  // Si cambia el commodity mientras hay un custom filter activo, re-lanza la búsqueda
  useEffect(() => {
    if (!appliedCustomFilter) return
    fetchInvoicesData(appliedCustomFilter).then(results => {
      setFilteredData(results)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commodity])
  // Hooks Region Ends

  // Handlers Region Starts

  const onSelectDateHandler = React.useCallback(
    (startDate: Date, endDate: Date, activeDateFilter?: DateFilters): void => {
      setFilteredData(undefined)
      setAppliedCustomFilter(undefined)
      setDateRange({ start: startDate, end: endDate })
      setActiveDateFilter(activeDateFilter ? activeDateFilter : DateFilters.TODAY)
    },
    []
  )

  const fetchInvoicesData = async (filterToApply: any): Promise<any[]> => {
    pendingFilterRef.current = filterToApply
    const clientType = defineClientType(
      activeTab !== undefined && activeTab !== null ? activeTab : currentTab
    )
    const activePageTab: TabsInvoicesType =
      activeTab !== undefined && activeTab !== null ? tabsArray[activeTab]?.id! : currentTab

    const response = await fetchInvoices({
      commodity,
      clientType,
      dateRange: { start: filterToApply.startDate, end: filterToApply.endDate },
      invoiceCode: filterToApply.invoiceCode,
      subjectCode: filterToApply.subjectCode,
      contractCode: filterToApply.contractCode,
      cupsCode: filterToApply.cupsCode,
      activePageTab,
      hasObjections: filterToApply.hasObjections,
      filterDateBy: filterToApply.dateFilterType ?? FilterDateBy.EMISSION
    })

    if (response.success && response.data) {
      return response.data
    }
    return []
  }

  const handleShowResults = (results: any[]) => {
    const filterToApply = pendingFilterRef.current
    if (!filterToApply) return
    setFilteredData(results)
    setAppliedCustomFilter(filterToApply)
    setDateRange(prev => ({
      start: filterToApply.startDate,
      end: filterToApply.endDate,
      customFilter: {
        ...prev.customFilter,
        dateFilterType: filterToApply.dateFilterType
      }
    }))
    setActiveDateFilter(undefined)
  }

  const onSelectCommodityHandler = (commodity: EnumCommodity): void => {
    setCommodity(commodity)
    storeCommodityFilterForCurrentPage(commodity)
    storeActiveCommodity(commodity)
  }

  const onTableFetching = (childIsFetching: boolean) => {
    setIsFetching(childIsFetching)
  }

  const onTabChangeHandler = (tab: number) => {
    if (currentTab !== tab) {
      setFilteredData(undefined)
      setAppliedCustomFilter(undefined)
      setIsFetching(true)
      setCurrentTab(tab)
      storeActiveTabForCurrentPage(tab)
      setGlobalNotification(undefined)
    }
  }

  const searchFilterInputs = [
    {
      name: 'dateFilterType',
      label: t('invoicesPage:filters.inputs.dateFilterType'),
      type: DataType.SELECT,
      selectOptions: enumToSelectOptionArray(FilterDateBy, 'enums:FilterDateBy')
    },
    {
      name: 'invoiceCode',
      label: t('invoicesPage:filters.inputs.invoiceCode'),
      type: DataType.TEXT,
      helpText: t('invoicesPage:filters.helpText.minChar')
    },
    {
      name: 'subjectCode',
      label: t('invoicesPage:filters.inputs.subjectCode'),
      type: DataType.TEXT,
      helpText: t('invoicesPage:filters.helpText.minChar')
    },
    {
      name: 'cupsCode',
      label: t('invoicesPage:filters.inputs.cupsCode'),
      type: DataType.TEXT,
      helpText: t('invoicesPage:filters.helpText.minChar')
    },
    {
      name: 'contractCode',
      label: t('invoicesPage:filters.inputs.contractCode'),
      type: DataType.TEXT,
      helpText: t('invoicesPage:filters.helpText.minChar')
    },
    {
      name: 'hasObjections',
      label: t('invoicesPage:filters.inputs.hasObjections'),
      type: DataType.SWITCH
    }
  ]

  // Handlers Region Ends

  // Functions Region Starts
  const renderTabContent = (activeTab: number) => {
    return (
      tabsArray[activeTab] && (
        <InvoicesTable
          clientType={defineClientType(activeTab)}
          activeTabInvoiceType={tabsArray[activeTab]?.id}
          commodity={commodity}
          dateRange={dateRange}
          activeDateFilter={activeDateFilter}
          dataTableFilter={dataTableFilter}
          customFilter={appliedCustomFilter}
          dataTableSearch={dataTableSearch}
          onFetching={onTableFetching}
          config={mapTableConfiguration(tabsArray[activeTab]?.id!)}
          data={filteredData}
        ></InvoicesTable>
      )
    )
  }

  const defineClientType = (tabIndex: number): EnumClientTypes => {
    switch (tabsArray[tabIndex]?.id) {
      case TabsInvoicesType.aggregatedInvoice_finalClient:
        return EnumClientTypes.FINAL_CLIENT
      case TabsInvoicesType.aggregatedInvoice_extendedClient:
        return EnumClientTypes.EXTENDED_CLIENT
      default:
        return tabsArray[tabIndex]?.id!
    }
  }

  const renderCommodities = () => {
    if (commodities !== undefined && commodities.length > 1) {
      return (
        <CommodityFilter
          commodities={[
            { commodity: enumValueToKey(EnumCommodity, commodities[0]) },
            { commodity: enumValueToKey(EnumCommodity, commodities[1]) }
          ]}
          commodity={commodity}
          enabled={!isFetching}
          inactive={false}
          onSelectCommodity={onSelectCommodityHandler}
        />
      )
    }
    return
  }

  // Functions Region Ends

  return (
    <Page notification={globalNotification}>
      {commodities && (
        <>
          <PageHeader title={t('invoicesPage:title')}>
            <div>
              <Grid container justify="flex-end">
                {renderCommodities()}
              </Grid>
            </div>
          </PageHeader>
          <Grid container justify="flex-end">
            <div style={{ marginRight: '16px' }}>
              <DateFilter
                inactive={filteredData !== undefined}
                enabled={!isFetching}
                initialFilter={DateFilters.TODAY}
                onSelectDate={onSelectDateHandler}
                filters={[
                  {
                    filter: DateFilters.TODAY,
                    label: t('translation:components.dateFilter.todayNoGas')
                  },
                  {
                    filter: DateFilters.LASTWEEK,
                    label: t('translation:components.dateFilter.lastWeek')
                  },
                  //   { filter: DateFilters.MONTH },
                  // { filter: DateFilters.LASTMONTH },
                  {
                    filter: DateFilters.RECORDS_LIM_500,
                    label: t('translation:components.dateFilter.invoicesLim500')
                  }
                ]}
              ></DateFilter>
            </div>
            <CustomFilterV2
              onFetchData={fetchInvoicesData}
              onShowResults={handleShowResults}
              additionalFilters={searchFilterInputs}
              appliedFilter={appliedCustomFilter}
              initialState={{ dateFilterType: FilterDateBy.EMISSION }}
              enabled={!isFetching}
              asPopover
            />
          </Grid>
          <TabsWithPermissions
            tabs={tabsDefinition}
            tabDefault={currentTab}
            onTabChange={onTabChangeHandler}
          ></TabsWithPermissions>
          {renderTabContent(activeTab ? activeTab : currentTab)}
        </>
      )}
    </Page>
  )
}
